/**
 * SessionDetailsTab — Details tab content for SessionDetailsModal.
 * Displays schedule info, product info, material info, timestamps,
 * production results, and notes.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Package, Truck, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../../../i18n/LanguageContext';
import { formatDateTime, getDateCode } from '../../../../utils/dateUtils';
import { formatYield, isYieldFallback } from '../../../../hooks/useYieldFactor';
import { encodeTransferId } from '../../../../utils/transferIdEncoder';
import CarryoverIndicator from '../../../../components/CarryoverIndicator';
import { deriveOutputCases } from '../../../../utils/outputCases';
import { getStatusBadgeClass } from '../../../../utils/statusColors';
import { getLotMaterialBadge, isFpNiCarryoverMaterial } from '../../../../utils/lotMaterialBadge';

interface SessionDetailsTabProps {
  session: any;
  subSessions?: any[];
  locationCodes: any[];
  canEdit: boolean;
  setSourceLocationMutation: any;
  onClose: () => void;
}

const SessionDetailsTab: React.FC<SessionDetailsTabProps> = ({
  session,
  subSessions = [],
  locationCodes,
  canEdit,
  setSourceLocationMutation,
  onClose,
}) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  // WCO-2470: the per-sub-session case breakdown is collapsed by default (expand via its header) so a session
  // with many sub-sessions doesn't overwhelm the panel with numbers.
  const [showSubBreakdown, setShowSubBreakdown] = useState(false);
  // WCO-2473: the per-destination (output location) case breakdown, same collapsed-by-default pattern.
  const [showDestBreakdown, setShowDestBreakdown] = useState(false);

  const rawMaterials = (session.materials || []).filter((m: any) => !isFpNiCarryoverMaterial(m));
  const fpCarryover = (session.materials || []).filter((m: any) => isFpNiCarryoverMaterial(m));
  const fpCarryoverTotal = fpCarryover.reduce((sum: number, m: any) => sum + (Number(m.allocated_weight) || 0), 0);

  return (
    <div className="space-y-6">
      {/* Row 1: Source Location (full width) */}
      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg px-4 py-2">
        <div className="flex items-center gap-3">
          <Truck className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
          <span className="text-sm text-gray-600 dark:text-gray-300 flex-shrink-0">
            {t('sessionDetails.sourceLocation') || 'Source Location'}:
          </span>

          {session.source_location ? (
            <span className="font-medium text-gray-900 dark:text-white">
              {locationCodes.find((l: any) => l.location_code === session.source_location)?.description || session.source_location}
            </span>
          ) : (
            <>
              <select
                disabled={!canEdit || setSourceLocationMutation.isLoading}
                onChange={(e) => {
                  if (e.target.value) {
                    setSourceLocationMutation.mutate(e.target.value);
                  }
                }}
                className="flex-1 max-w-xs px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                defaultValue=""
              >
                <option value="">{t('sessionDetails.selectSourceLocation') || 'Select...'}</option>
                {locationCodes.map((loc: any, idx: number) => (
                  <option key={loc.location_code ?? `loc-${idx}`} value={loc.location_code}>
                    {loc.location_code} - {loc.description}
                  </option>
                ))}
              </select>
              {setSourceLocationMutation.isLoading && (
                <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
              )}
            </>
          )}
        </div>

        {/* Material source breakdown — aggregate by transfer_number */}
        {session.materials && session.materials.length > 0 && (() => {
          const rawMaterialsForSources = session.materials.filter((m: any) => !isFpNiCarryoverMaterial(m));
          const grouped = new Map<string, { transfer_number: string; weights: number[]; totalWeight: number; isLinkedTransfer: boolean; isOnHold: boolean; material: any }>();
          for (const m of rawMaterialsForSources) {
            const key = m.transfer_number || '';
            const existing = grouped.get(key);
            if (existing) {
              existing.weights.push(Number(m.allocated_weight));
              existing.totalWeight += Number(m.allocated_weight);
            } else {
              grouped.set(key, {
                transfer_number: m.transfer_number,
                weights: [Number(m.allocated_weight)],
                totalWeight: Number(m.allocated_weight),
                isLinkedTransfer: m.transfer_number === session.linked_transfer_number,
                isOnHold: m.lot_approval_status === 'pending',
                material: m,
              });
            }
          }
          return (
            <div className="mt-2 ml-7 space-y-1">
              {Array.from(grouped.values()).map((g, idx) => {
                const { isLinkedTransfer, isOnHold, material: m } = g;
                const weightDisplay = g.weights.length > 1
                  ? `${g.weights.map(w => w.toFixed(2)).join(' + ')} lbs`
                  : `${g.totalWeight.toFixed(2)} lbs`;
                return (
                  <div key={`${g.transfer_number}-${idx}`}>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="font-mono font-medium text-gray-700 dark:text-gray-300">
                        WT {g.transfer_number}
                      </span>
                      <span className="text-gray-500 dark:text-gray-400">
                        {t('sessionDetails.allocated')}: {weightDisplay}
                      </span>
                      {!isLinkedTransfer && !isOnHold && (
                        <span className="text-gray-400 dark:text-gray-500 italic">
                          {t('sessionDetails.existingInventory')}
                        </span>
                      )}
                      {isLinkedTransfer && (
                        <button
                          onClick={() => {
                            onClose();
                            navigate(`/transfers/${encodeTransferId(g.transfer_number)}`);
                          }}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md shadow-sm transition-colors"
                        >
                          {t('sessionDetails.viewTransfer')}
                        </button>
                      )}
                    </div>
                    {isOnHold && (
                      <div className="flex items-center gap-2 text-xs mt-0.5 ml-2">
                        <span className="px-1 py-0.5 font-medium rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                          {t('supervisor.materialOnHold')}
                        </span>
                        <span className="text-red-600 dark:text-red-500">
                          {t('sessionDetails.totalOnHold')}: {Number(m.lot_on_hold_weight).toFixed(2)} lbs ({Number(m.lot_on_hold_quantity)} cs)
                        </span>
                        {m.lot_on_hold_reason && (
                          <span className="text-red-600 dark:text-red-500">— {m.lot_on_hold_reason}</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>

      {/* Row 2: Product Information and Material Information (2 columns) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Product Information */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 rounded-lg p-4 border border-indigo-200 dark:border-indigo-700">
          <div className="flex items-center gap-2 mb-3">
            <Package className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {t('sessionDetails.productInfo')}
            </h3>
          </div>
          {(() => {
            // Use backend's session.target_output_weight directly
            // Backend already excludes cancelled sub-sessions via updateParentSessionTotals()
            let estimatedOutput = Number(session.target_output_weight) || 0;

            // Fallback: calculate from materials with yield factor.
            // WCO-2608: yield ONLY the raw materials, then ADD the FP-NI carryover (already
            // finished product) after yield — never multiply the carryover by yield.
            if (estimatedOutput <= 0) {
              const rawWeight = rawMaterials.length > 0
                ? rawMaterials.reduce((sum: number, m: any) => sum + (Number(m.allocated_weight) || 0), 0)
                // WCO-2608: initial_material_weight is raw material only (schema), so use it directly.
                : Number(session.initial_material_weight) || 0;
              // yield_factor is decimal (e.g. 0.9450), use directly
              const yieldFactor = rawMaterials.length > 0 && rawMaterials[0].yield_factor
                ? Number(rawMaterials[0].yield_factor)
                : Number(session.raw_material_yield_factor) || 0;
              estimatedOutput = rawWeight * yieldFactor + fpCarryoverTotal;
            }

            // If still no output, calculate from total_boxes × unit_weight
            const totalBoxes = Number(session.total_boxes) || 0;
            const productUnitWeight = Number(session.unit_weight) || 0;
            if (estimatedOutput <= 0 && totalBoxes > 0 && productUnitWeight > 0) {
              estimatedOutput = totalBoxes * productUnitWeight;
            }

            // Use expected_boxes from backend (kept in sync when material is added)
            const estimatedCases = Number(session.expected_boxes) || totalBoxes || 0;

            // Yield: backend now returns averaged yield_percentage from sub-sessions on the session response.
            const completedYield = session.yield_percentage;

            // Actual cases = packed boxes, tracked per sub-session (sum across sub-sessions).
            const actualCases = subSessions.reduce((sum: number, ss: any) => sum + (Number(ss.boxes_produced) || 0), 0);

            // Compute values for tooltips.
            // WCO-2608: the tooltip weight is RAW material only (exclude FP-NI carryover). The
            // carryover (fpCarryoverTotal) is rendered as a separate "+ N carryover" term added
            // after yield, mirroring the Estimated Output formula.
            const totalMaterialWeightDisplay = rawMaterials.length > 0
              ? rawMaterials.reduce((sum: number, m: any) => sum + (Number(m.allocated_weight) || 0), 0)
              // WCO-2608: initial_material_weight is raw material only (schema), so use it directly.
              : Number(session.initial_material_weight) || 0;
            const yieldFactorDisplay = rawMaterials.length > 0 && rawMaterials[0].yield_factor
              ? Number(rawMaterials[0].yield_factor)
              : Number(session.raw_material_yield_factor) || 0;

            return (
              <>
                {/* Product header - SKU, English name, Chinese name on one line */}
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 mb-3">
                  <span className="text-base font-bold text-gray-900 dark:text-white">{session.product_sku}</span>
                  {session.product_name && (
                    <span className="text-sm text-gray-600 dark:text-gray-300">{session.product_name}</span>
                  )}
                  {session.product_chinese_name && (
                    <span className="text-sm text-gray-600 dark:text-gray-300">{session.product_chinese_name}</span>
                  )}
                </div>
                {/* Product details - 2 rows × 4 cols: top row = production results, bottom row = planned values */}
                <div className="grid grid-cols-4 gap-2">
                  {/* Top row: production results (mirrors the bottom row column-by-column) */}
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('sessionDetails.actualOutput')}</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {session.status === 'completed' && session.actual_output_weight
                        ? `${Number(session.actual_output_weight).toFixed(2)} lbs`
                        : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('sessionDetails.wasteWeight')}</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {session.status === 'completed' && session.waste_weight
                        ? `${Number(session.waste_weight).toFixed(2)} lbs`
                        : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('operator.yield')}</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {session.status === 'completed' && completedYield
                        ? `${completedYield}%`
                        : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('sessionDetails.actualCases')}</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {session.status === 'completed' && actualCases > 0 ? actualCases : '-'}
                    </p>
                  </div>
                  {/* Bottom row: planned values */}
                  <div className="relative group">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('sessionDetails.estimatedOutput')}</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white underline decoration-dotted cursor-help">
                      {estimatedOutput > 0 ? `${estimatedOutput.toFixed(2)} lbs` : '-'}
                    </p>
                    {/* WCO-2608: the "↳ N lbs carryover" line belongs directly under the Estimated
                        Output value it annotates (it was previously orphaned at the bottom of the
                        card, below the cases dropdowns). Renders nothing when carryover is 0. */}
                    <CarryoverIndicator allocations={fpCarryover} className="mt-1" />
                    {/* WCO-2608: also show the tooltip for a carryover-only session (raw weight 0)
                        so the "+ N carryover" term is still visible. */}
                    {estimatedOutput > 0 && (fpCarryoverTotal > 0 || (totalMaterialWeightDisplay > 0 && yieldFactorDisplay > 0)) && (
                      <div className="absolute bottom-full left-0 mb-1 hidden group-hover:block z-50">
                        <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap">
                          {/* Compute the RHS from the LHS so the equation is always internally consistent.
                              Do NOT paste the stored target_output_weight as the RHS — if the input weight
                              and target were derived on different bases the equation renders false math
                              (e.g. "137.14 × 87.5% = 149.63", which is arithmetically wrong). WCO-2407.
                              WCO-2608: the FP-NI carryover is finished product — add it AFTER yield as its
                              own "+ N carryover" term, never fold it into the yielded weight. */}
                          <p>{totalMaterialWeightDisplay.toFixed(2)} lbs × {(yieldFactorDisplay * 100).toFixed(2)}%{fpCarryoverTotal > 0 ? ` + ${fpCarryoverTotal.toFixed(2)} ${t('sessionDetails.carryover')}` : ''} = {(totalMaterialWeightDisplay * yieldFactorDisplay + fpCarryoverTotal).toFixed(2)} lbs</p>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="relative group">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('productionProgress.totalWeight')}</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white underline decoration-dotted cursor-help">
                      {estimatedCases > 0 && productUnitWeight > 0 ? `${(estimatedCases * productUnitWeight).toFixed(2)} lbs` : '-'}
                    </p>
                    {estimatedCases > 0 && productUnitWeight > 0 && (
                      <div className="absolute bottom-full left-0 mb-1 hidden group-hover:block z-50">
                        <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap">
                          <p>{estimatedCases} cs × {productUnitWeight.toFixed(2)} lbs = {(estimatedCases * productUnitWeight).toFixed(2)} lbs</p>
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('sessionDetails.packing')}</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{session.packing || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('sessionDetails.estimatedCases')}</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{estimatedCases > 0 ? estimatedCases : '-'}</p>
                  </div>
                </div>

                {/* WCO-2470: per-sub-session case breakdown. Only for split sessions (>1 sub-session) — for a
                    single sub-session the session-level figures above already tell the whole story. Expected =
                    sub.expected_boxes (the WCO-2469 single-source-of-truth FLOOR, so the rows sum exactly to the
                    session's Estimated Cases); actual = sub.boxes_produced (packed). Actuals shown regardless of
                    parent status — a sub-session can be completed while the parent is still Active. */}
                {subSessions.length > 1 && (
                  <div className="mt-3">
                    {/* WCO-2470: collapsed by default. The header toggles the breakdown and always shows the
                        Total expected/actual so there's at-a-glance info without expanding. */}
                    <button
                      type="button"
                      onClick={() => setShowSubBreakdown((v) => !v)}
                      className="w-full flex items-center gap-1.5 mb-1.5 text-left"
                    >
                      {showSubBreakdown
                        ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                        : <ChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('sessionDetails.perSubSessionCases')}</span>
                      <span className="text-[10px] text-gray-400">({subSessions.length})</span>
                      {/* Collapsed summary: Total expected / actual. */}
                      {!showSubBreakdown && (
                        <span className="ml-auto text-xs tabular-nums text-gray-500 dark:text-gray-400">
                          {subSessions.reduce((s: number, ss: any) => s + (Number(ss.expected_boxes) || 0), 0)} / {actualCases}
                        </span>
                      )}
                    </button>
                    {showSubBreakdown && (
                    <div className="space-y-1">
                      {/* Column header */}
                      <div className="flex items-center gap-2 px-2 text-[10px] uppercase tracking-wide text-gray-400">
                        <span className="flex-1">{t('sessionDetails.station')}</span>
                        <span className="w-16 text-right">{t('sessionDetails.expectedCasesShort')}</span>
                        <span className="w-16 text-right">{t('sessionDetails.actualCasesShort')}</span>
                      </div>
                      {subSessions.map((sub: any) => {
                        const subExpected = Number(sub.expected_boxes) || 0;
                        const subActual = Number(sub.boxes_produced) || 0;
                        return (
                          <div
                            key={sub.sub_session_number}
                            className="flex items-center gap-2 rounded-md bg-gray-50 dark:bg-gray-700/40 px-2 py-1.5"
                          >
                            <span className="flex-1 min-w-0 flex items-center gap-1.5">
                              {/* Station name when assigned; a muted placeholder otherwise (no duplicate of the
                                  sub-session number). The sub-session number is always shown as the mono id. */}
                              <span className={`text-sm truncate ${sub.station_name ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500 italic'}`}>
                                {sub.station_name || t('sessionDetails.noStationYet')}
                              </span>
                              <span className="font-mono text-[10px] text-gray-400 shrink-0">{sub.sub_session_number}</span>
                              {sub.status && (
                                <span className={`shrink-0 px-1.5 py-0.5 rounded-full text-[9px] font-medium ${getStatusBadgeClass(sub.status)}`}>
                                  {t(`status.${sub.status}`) || sub.status}
                                </span>
                              )}
                            </span>
                            <span className="w-16 text-right text-sm tabular-nums text-gray-700 dark:text-gray-300">{subExpected}</span>
                            <span className={`w-16 text-right text-sm tabular-nums font-medium ${subActual >= subExpected && subExpected > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-900 dark:text-white'}`}>{subActual}</span>
                          </div>
                        );
                      })}
                      {/* Total row — sums to the session's Estimated / Actual Cases above (WCO-2469 invariant). */}
                      <div className="flex items-center gap-2 px-2 pt-1 border-t border-gray-200 dark:border-gray-600 text-xs font-semibold text-gray-900 dark:text-white">
                        <span className="flex-1">{t('common.total')}</span>
                        <span className="w-16 text-right tabular-nums">{subSessions.reduce((s: number, ss: any) => s + (Number(ss.expected_boxes) || 0), 0)}</span>
                        <span className="w-16 text-right tabular-nums">{actualCases}</span>
                      </div>
                    </div>
                    )}
                  </div>
                )}

                {/* WCO-2473: per-destination (output location) case breakdown, shown directly under the
                    WCO-2470 sub-session block. Same collapsible pattern. Rendered when the session has >=1
                    active output destination. Expected = the per-destination split DERIVED against the
                    session's Estimated Cases (WCO-2477 — see deriveOutputCases), so the total tracks
                    "Cases by Sub-Session" and never shows the pre-edit plan (e.g. 7 after a 4-box edit);
                    actual = this session's finished boxes scanned into that output. Destinations resolve
                    to CODE — Description. */}
                {Array.isArray(session.transfer_back_destinations) && session.transfer_back_destinations.length > 0 && (() => {
                  const dests = deriveOutputCases(session.transfer_back_destinations, estimatedCases);
                  const destExpected = dests.reduce((s: number, d: any) => s + (Number(d.expected_cases ?? d.cases) || 0), 0);
                  const destActual = dests.reduce((s: number, d: any) => s + (Number(d.actual_cases) || 0), 0);
                  return (
                    <div className="mt-3">
                      {/* Collapsed by default; header always shows Total expected / actual. */}
                      <button
                        type="button"
                        onClick={() => setShowDestBreakdown((v) => !v)}
                        className="w-full flex items-center gap-1.5 mb-1.5 text-left"
                      >
                        {showDestBreakdown
                          ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                          : <ChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('sessionDetails.perOutputLocationCases')}</span>
                        <span className="text-[10px] text-gray-400">({dests.length})</span>
                        {!showDestBreakdown && (
                          <span className="ml-auto text-xs tabular-nums text-gray-500 dark:text-gray-400">
                            {destExpected} / {destActual}
                          </span>
                        )}
                      </button>
                      {showDestBreakdown && (
                        <div className="space-y-1">
                          {/* Column header */}
                          <div className="flex items-center gap-2 px-2 text-[10px] uppercase tracking-wide text-gray-400">
                            <span className="flex-1">{t('sessionDetails.outputLocation')}</span>
                            <span className="w-16 text-right">{t('sessionDetails.expectedCasesShort')}</span>
                            <span className="w-16 text-right">{t('sessionDetails.actualCasesShort')}</span>
                          </div>
                          {dests.map((d: any, idx: number) => {
                            const dExpected = Number(d.expected_cases ?? d.cases) || 0;
                            const dActual = Number(d.actual_cases) || 0;
                            return (
                              <div
                                key={d.location_code || idx}
                                className="flex items-center gap-2 rounded-md bg-gray-50 dark:bg-gray-700/40 px-2 py-1.5"
                              >
                                {/* CODE — Description (falls back to just the code when no name is resolved). */}
                                <span className="flex-1 min-w-0 truncate text-sm">
                                  <span className="font-mono text-[11px] text-gray-500 dark:text-gray-400">{d.location_code}</span>
                                  {d.destination_name && d.destination_name !== d.location_code && (
                                    <span className="text-gray-900 dark:text-gray-100"> — {d.destination_name}</span>
                                  )}
                                </span>
                                <span className="w-16 text-right text-sm tabular-nums text-gray-700 dark:text-gray-300">{dExpected}</span>
                                <span className={`w-16 text-right text-sm tabular-nums font-medium ${dActual >= dExpected && dExpected > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-900 dark:text-white'}`}>{dActual}</span>
                              </div>
                            );
                          })}
                          {/* Total row */}
                          <div className="flex items-center gap-2 px-2 pt-1 border-t border-gray-200 dark:border-gray-600 text-xs font-semibold text-gray-900 dark:text-white">
                            <span className="flex-1">{t('common.total')}</span>
                            <span className="w-16 text-right tabular-nums">{destExpected}</span>
                            <span className="w-16 text-right tabular-nums">{destActual}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </>
            );
          })()}
        </div>

        {/* Raw Material Information */}
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Package className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {t('sessionDetails.materialInfo')}
              {rawMaterials.length > 1 && (
                <span className="ml-2 text-xs font-normal text-gray-500">
                  ({rawMaterials.length} {t('production.materials').toLowerCase()})
                </span>
              )}
            </h3>
          </div>

          {/* Display all materials if available, otherwise fall back to legacy single material */}
          {rawMaterials.length > 0 ? (
            <div className="space-y-4">
              {/* Group materials by item_number for compact display */}
              {(() => {
                const groupedMaterials = rawMaterials.reduce((groups: any, material: any) => {
                  const key = material.item_number;
                  if (!groups[key]) {
                    groups[key] = {
                      item_number: material.item_number,
                      item_english_name: material.item_english_name,
                      item_chinese_name: material.item_chinese_name,
                      brand: material.brand,
                      yield_factor: material.yield_factor,
                      yield_factor_source: material.yield_factor_source,
                      unit_weight: material.unit_weight,
                      lots: []
                    };
                  }
                  groups[key].lots.push({
                    transfer_number: material.transfer_number,
                    allocated_weight: material.allocated_weight,
                    cases: material.cases,
                    material_location_code: material.material_location_code,
                    date_code: material.date_code,
                    lot_status: material.lot_status,
                    lot_approval_status: material.lot_approval_status,
                    lot_on_hold_reason: material.lot_on_hold_reason,
                    lot_on_hold_weight: material.lot_on_hold_weight,
                    lot_on_hold_quantity: material.lot_on_hold_quantity,
                    // WCO-2607: per-lot intact/not-intact + source-lot status, for the badge.
                    lot_is_ni_remnant: material.lot_is_ni_remnant,
                    linked_lot_status: material.linked_lot_status,
                    linked_lot_type: material.linked_lot_type,
                    // WCO-2613: outbound remainder carryover — [{ destination_session_number,
                    // is_ni_remnant, weight_carried, cases_carried }] — the mirror subtraction shown on
                    // the source session (intact = cases+weight, non-intact = weight only "(partial)").
                    outbound_remainder_to: material.outbound_remainder_to,
                    // WCO-2613 follow-up: inbound remainder provenance — the source session this row's
                    // consumed remainder lot came from (null when pooled or not a remainder lot).
                    inbound_remainder_from: material.inbound_remainder_from,
                    // WCO-2659: FROZEN remainder produced by THIS session — [{ is_ni_remnant,
                    // weight_produced, cases_produced }]. Shown even before anything consumes it; the
                    // frozen amounts (actual_weight / actual_quantity) don't shrink on downstream use.
                    remainder_produced: material.remainder_produced,
                  });
                  return groups;
                }, {} as Record<string, { item_number: string; item_english_name?: string; item_chinese_name?: string; brand?: string; yield_factor?: number; yield_factor_source?: string; lots: { transfer_number?: string; allocated_weight?: number; cases?: number; material_location_code?: string; date_code?: string }[] }>);

                return Object.values(groupedMaterials).map((group: any, groupIndex: number) => {
                  const groupTotal = group.lots.reduce((sum: number, lot: any) => sum + (Number(lot.allocated_weight) || 0), 0);
                  return (
                    <div key={group.item_number} className={groupIndex > 0 ? 'pt-4 border-t border-gray-200 dark:border-gray-600' : ''}>
                      {/* Material header - shared info */}
                      <div className="mb-3">
                        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                          <span className="text-base font-bold text-gray-900 dark:text-white">{group.item_number}</span>
                          {group.item_english_name && group.item_english_name !== group.item_number && (
                            <span className="text-sm text-gray-600 dark:text-gray-300">{group.item_english_name}</span>
                          )}
                          {group.item_chinese_name && (
                            <span className="text-sm text-gray-600 dark:text-gray-300">{group.item_chinese_name}</span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mt-1">
                          <span className="text-xs text-gray-500 dark:text-gray-400">{t('supervisor.scheduleBrand')}: <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{group.brand || '-'}</span></span>
                          <span className="text-xs text-gray-400 dark:text-gray-500">|</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{t('sessionDetails.yieldFactor')}: {(() => {
                            const yieldValue = Number(group.yield_factor) || 0;
                            if (yieldValue > 0) {
                              return <span className={`text-sm font-medium ${isYieldFallback(group.yield_factor_source) ? 'text-amber-600 dark:text-amber-400' : 'text-gray-700 dark:text-gray-200'}`}>{formatYield(yieldValue, undefined, group.yield_factor_source)}</span>;
                            } else {
                              return <span className="text-sm font-medium text-gray-400 dark:text-gray-500">?</span>;
                            }
                          })()}</span>
                          <span className="text-xs text-gray-400 dark:text-gray-500">|</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{t('sessionDetails.unitWeight')}: <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{group.unit_weight ? `${group.unit_weight} lbs` : '-'}</span></span>
                        </div>
                      </div>
                      {/* Lots table - compact rows */}
                      <div className="ml-2 border-l-2 border-gray-300 dark:border-gray-600 pl-3">
                        <div className="grid grid-cols-[1.5fr_1fr_0.6fr_1fr] gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1">
                          <span>{t('common.lotNumber')}</span>
                          <span>{t('sessionDetails.initialWeight')}</span>
                          <span>Cases</span>
                          <span>{t('supervisor.scheduleLocation')}</span>
                        </div>
                        {group.lots.map((lot: any, lotIndex: number) => (
                          <div key={`${lot.transfer_number}-${lotIndex}`}>
                            <div className="grid grid-cols-[1.5fr_1fr_0.6fr_1fr] gap-2 text-sm py-0.5 items-start">
                              <span className="font-medium text-gray-900 dark:text-white">
                                {/* WT number on its own non-wrapping line; tags/badge stacked beneath
                                    so a narrow column never breaks the number mid-word (WCO-2607). */}
                                <span className="whitespace-nowrap">
                                  {lot.transfer_number ? `WT ${lot.transfer_number}` : '-'}
                                </span>
                                {/* WCO-2607: origin badge so two lots of the same transfer are
                                    distinguishable. Transfer-origin stock (the transfer's own shipped
                                    lot: consumed/available) shows "Transfer"; material added on top
                                    shows Intact / Not-intact / By-product / On-hold. */}
                                {(() => {
                                  const badge = getLotMaterialBadge({
                                    lotType: lot.linked_lot_type,
                                    status: lot.linked_lot_status,
                                    legacyStatus: lot.linked_lot_status,
                                    isNiRemnant: lot.lot_is_ni_remnant,
                                    rawBadge: 'transfer',
                                  }, t);
                                  if (!badge) return null;
                                  return (
                                    <span className={`mt-0.5 inline-block w-fit px-1.5 py-0.5 rounded-full text-[10px] font-normal whitespace-nowrap ${badge.cls}`}>
                                      {badge.label}
                                    </span>
                                  );
                                })()}
                              </span>
                              <span className="relative group text-gray-700 dark:text-gray-300">
                                <span className="underline decoration-dotted cursor-help">{lot.allocated_weight ? `${Number(lot.allocated_weight).toFixed(2)} lbs` : '-'}</span>
                                {lot.allocated_weight && Number(group.yield_factor) > 0 && (
                                  <div className="absolute bottom-full left-0 mb-1 hidden group-hover:block z-50">
                                    <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap">
                                      <p>{Number(lot.allocated_weight).toFixed(2)} lbs × {(Number(group.yield_factor) * 100).toFixed(2)}% = {(Number(lot.allocated_weight) * Number(group.yield_factor)).toFixed(2)} lbs {t('sessionDetails.estimatedOutput').toLowerCase()}</p>
                                    </div>
                                  </div>
                                )}
                              </span>
                              <span className="text-gray-700 dark:text-gray-300">
                                {lot.cases ? lot.cases : '-'}
                              </span>
                              <span className="text-gray-600 dark:text-gray-400">{lot.material_location_code || '-'}</span>
                            </div>
                            {/* WCO-2659: FROZEN "remainder produced" — the amount of intact + not-intact remainder THIS
                                session produced, shown even before anything consumes it (the WCO-2613 −carried-to lines
                                below only cover the consumed portion). Same per-(item,transfer) payload + first-lot-of-
                                transfer de-dupe as the carried-to block; renders ABOVE it. Compact weight-first: total
                                lbs, with a parenthetical breakdown ONLY when both intact and NI are present. Intact →
                                "N cases intact" (frozen actual_quantity); NI → "W partial" (weight only, no cases). */}
                            {group.lots.findIndex((l: any) => l.transfer_number === lot.transfer_number) === lotIndex &&
                              Array.isArray(lot.remainder_produced) && lot.remainder_produced.length > 0 && (() => {
                                const rp = lot.remainder_produced as any[];
                                const intact = rp.find((r) => !r.is_ni_remnant);
                                const ni = rp.find((r) => r.is_ni_remnant);
                                const intactW = Number(intact?.weight_produced) || 0;
                                const intactC = Number(intact?.cases_produced) || 0;
                                const niW = Number(ni?.weight_produced) || 0;
                                const totalW = intactW + niW;
                                // Breakdown parts — only assembled when both types coexist (the one case the folded
                                // weight needs disambiguating). Single-type falls through to the simple forms below.
                                if (intact && ni) {
                                  const intactPart = intactC > 0
                                    ? t('sessionDetails.remainderProducedIntactPart').replace('{{cases}}', String(intactC))
                                    : t('sessionDetails.remainderProducedIntactWeightPart');
                                  const niPart = t('sessionDetails.remainderProducedPartialPart').replace('{{weight}}', niW.toFixed(2));
                                  const line = t('sessionDetails.remainderProduced')
                                    .replace('{{weight}}', totalW.toFixed(2))
                                    .replace('{{breakdown}}', `${intactPart} + ${niPart}`);
                                  return <div className="ml-2 mt-0.5 mb-1 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">↳ {line}</div>;
                                }
                                // Intact only → cases + weight (or weight-only when cases are a data gap).
                                if (intact) {
                                  const line = (intactC > 0
                                    ? t('sessionDetails.remainderProducedIntact').replace('{{cases}}', String(intactC))
                                    : t('sessionDetails.remainderProducedWeightOnly'))
                                    .replace('{{weight}}', intactW.toFixed(2));
                                  return <div className="ml-2 mt-0.5 mb-1 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">↳ {line}</div>;
                                }
                                // NI only → weight-only "(partial)".
                                const line = t('sessionDetails.remainderProducedPartial').replace('{{weight}}', niW.toFixed(2));
                                return <div className="ml-2 mt-0.5 mb-1 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">↳ {line}</div>;
                              })()}
                            {/* WCO-2659: the OUTBOUND "−carried to session X" lines (WCO-2613) were removed from the
                                source session. They were redundant — the RECEIVING session already states the origin
                                via its "+carried from session X" line (kept below) — and unbounded: a remainder spread
                                across many sessions produced one line per destination, bloating the panel. The frozen
                                "remaining material" line above is the source session's summary; provenance of where it
                                went lives on each receiving session. (outbound_remainder_to is still returned by the
                                backend but no longer rendered.) */}
                            {/* WCO-2613 follow-up: INBOUND remainder provenance — this row consumed a remainder lot;
                                show the source session it came from. Per-lot (not per transfer), so intact and
                                not-intact rows of the same transfer each show their own origin. Uses this row's own
                                weight/cases; intact → cases + weight, non-intact → weight only "(partial)". */}
                            {lot.inbound_remainder_from && (
                              <div key={`carry-from-${lot.inbound_remainder_from}`} className="ml-2 mt-0.5 mb-1 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                ↳ {(lot.lot_is_ni_remnant
                                    // non-intact: opened/loose, no case concept → weight only "(partial)"
                                    ? t('sessionDetails.carriedFromSessionPartial')
                                    : Number(lot.cases) > 0
                                      // intact with a real case count → cases + weight
                                      ? t('sessionDetails.carriedFromSession').replace('{{cases}}', String(Number(lot.cases)))
                                      // intact but cases unrecorded (0 = data gap, not "partial") → weight only, no label
                                      : t('sessionDetails.carriedFromSessionWeightOnly'))
                                  .replace('{{weight}}', (Number(lot.allocated_weight) || 0).toFixed(2))
                                  .replace('{{session}}', String(lot.inbound_remainder_from ?? ''))}
                              </div>
                            )}
                            {lot.lot_approval_status === 'pending' && (
                              <div className="ml-2 mt-0.5 mb-1 px-2 py-1 rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-xs">
                                <div className="flex items-center gap-3">
                                  <span className="font-medium text-red-700 dark:text-red-400">{t('supervisor.materialOnHold')}</span>
                                  {lot.lot_on_hold_weight && (
                                    <span className="text-red-600 dark:text-red-500">{t('sessionDetails.initialWeight')}: {Number(lot.lot_on_hold_weight).toFixed(2)} lbs</span>
                                  )}
                                  {lot.lot_on_hold_quantity && (
                                    <span className="text-red-600 dark:text-red-500">Qty: {Number(lot.lot_on_hold_quantity)}</span>
                                  )}
                                  {lot.lot_on_hold_reason && (
                                    <span className="text-red-600 dark:text-red-500">{t('common.reason')}: {lot.lot_on_hold_reason}</span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                        {/* Group total */}
                        {group.lots.length > 1 && (
                          <div className="grid grid-cols-[1.5fr_1fr_0.6fr_1fr] gap-2 text-sm pt-1 mt-1 border-t border-gray-200 dark:border-gray-600">
                            <span className="font-medium text-gray-700 dark:text-gray-300">{t('supervisor.totalLbs')}</span>
                            <span className="font-semibold text-gray-900 dark:text-white">{groupTotal.toFixed(2)} lbs</span>
                            {(() => {
                              const totalGroupCases = group.lots.reduce((sum: number, l: any) => sum + (Number(l.cases) || 0), 0);
                              const hteLots = group.lots.filter((l: any) => l.transfer_number !== session.linked_transfer_number);
                              const transferLots = group.lots.filter((l: any) => l.transfer_number === session.linked_transfer_number);
                              const hteCases = hteLots.reduce((sum: number, l: any) => sum + (Number(l.cases) || 0), 0);
                              const transferCases = transferLots.reduce((sum: number, l: any) => sum + (Number(l.cases) || 0), 0);
                              const hasTransfer = transferCases > 0;
                              return (
                                <div className="relative group/cases">
                                  <span className="font-semibold text-gray-900 dark:text-white underline decoration-dotted cursor-default">
                                    {totalGroupCases}
                                  </span>
                                  {(hasTransfer || hteCases > 0) && (
                                    <div className="absolute bottom-full left-0 mb-1 hidden group-hover/cases:block z-50">
                                      <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap">
                                        {hteCases > 0 && <p><span className="text-green-400">HTE:</span> {hteCases} cs</p>}
                                        {hasTransfer && <p><span className="text-amber-400">Transfer:</span> {transferCases} cs</p>}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                            <span></span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                });
              })()}
              {/* Grand total when multiple material groups */}
              {Object.keys(session.materials.reduce((g: any, m: any) => ({ ...g, [m.item_number]: true }), {})).length > 1 && (
                <div className="pt-3 border-t border-gray-300 dark:border-gray-500">
                  <div className="flex justify-between items-center">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('supervisor.totalLbs')}</p>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">
                      {rawMaterials.reduce((sum: number, m: any) => sum + (Number(m.allocated_weight) || 0), 0).toFixed(2)} lbs
                    </p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Legacy single material display */
            <>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('supervisor.material')}</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{session.raw_material_item_number || '-'}</p>
                  {session.raw_material_name && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">{session.raw_material_name}</p>
                  )}
                  {session.raw_material_chinese_name && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">{session.raw_material_chinese_name}</p>
                  )}
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('sessionDetails.initialWeight')}</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {session.initial_material_weight ? `${Number(session.initial_material_weight).toFixed(2)} lbs` : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('sessionDetails.yieldFactor')}</p>
                  <p className={`text-sm font-medium ${!Number(session.raw_material_yield_factor) ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white'}`}>
                    {Number(session.raw_material_yield_factor) > 0 ? formatYield(Number(session.raw_material_yield_factor)) : '?'}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('sessionDetails.unitWeight')}</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {session.unit_weight ? `${Number(session.unit_weight).toFixed(2)} lbs` : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('supervisor.scheduleBrand')}</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{session.brand || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('supervisor.scheduleLocation')}</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{session.material_location_code || '-'}</p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Row 3: Schedule Information (full width) */}
      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          <h3 className="font-semibold text-gray-900 dark:text-white">
            {t('sessionDetails.scheduleInfo')}
          </h3>
        </div>
        {/* 4-col grid. Left 3 columns hold two rows of single-height cells
            (row 1: Date, Date Code, Created By · row 2: Created At, Started, Completed).
            Column 4 is ONE cell spanning both rows (md:row-span-2) for Transfer Back
            Location(s) — so its stacked multi-line destinations don't inflate the
            height of the left cells. Transfer Back is placed explicitly (and first in
            the DOM) so grid auto-placement flows the other cells around it into
            columns 1-3. Merged the old separate Timestamps container here (WCO-2522). */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-2 md:gap-x-3 gap-y-3">
          {/* Column 4, spanning both rows */}
          <div className="md:col-start-4 md:row-start-1 md:row-span-2">
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('sessionDetails.transferBackLocation')}</p>
            {/* WCO-2522: transfer-back destinations moved to the multi-destination
                transfer_back_destinations array (WCO-2322/2473); the legacy
                transfer_back_location scalar is no longer populated. Render each
                destination as "CODE — Description", one per line for multiples,
                falling back to the legacy scalar for old sessions. */}
            {Array.isArray(session.transfer_back_destinations) && session.transfer_back_destinations.length > 0 ? (
              <div className="text-sm font-medium text-gray-900 dark:text-white space-y-0.5">
                {session.transfer_back_destinations.map((d: any, idx: number) => (
                  <p key={d.location_code || idx}>
                    {d.destination_name && d.destination_name !== d.location_code
                      ? `${d.location_code} — ${d.destination_name}`
                      : d.location_code}
                  </p>
                ))}
              </div>
            ) : (
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {session.transfer_back_location || '-'}
              </p>
            )}
          </div>
          {/* Row 1, columns 1-3 */}
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('supervisor.scheduleDate')}</p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {session.scheduled_date
                ? new Date(session.scheduled_date.split('T')[0] + 'T00:00:00').toLocaleDateString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric',
                  })
                : '-'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Date Code</p>
            <p className="text-sm font-mono font-medium text-indigo-600 dark:text-indigo-400">
              {session.date_code || (session.scheduled_date ? getDateCode(session.scheduled_date.split('T')[0]) : '-')}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('sessionDetails.createdBy')}</p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">{session.created_by_name || '-'}</p>
          </div>
          {/* Row 2, columns 1-3 — timestamps */}
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('sessionDetails.createdAt')}</p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {formatDateTime(session.created_at)}
            </p>
          </div>
          {session.started_at && (
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">{t('operator.startedAt')}</p>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {formatDateTime(session.started_at)}
              </p>
            </div>
          )}
          {session.completed_at && (
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">{t('sessionDetails.completedAt')}</p>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {formatDateTime(session.completed_at)}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Notes */}
      {session.notes && (
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
            {t('sessionDetails.notes')}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-300">{session.notes}</p>
        </div>
      )}
    </div>
  );
};

export default SessionDetailsTab;
